import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CarsWidgetFormType } from '../models/cars-widget-form.interfaces';
import { CarsWidget } from '../models/cars-widget.interface';
import {
  validateDateRange,
  validatePickupDropoffTimeNotPassed
} from '../utils/cars-validation.utils';
import { CarsFormValidationHelperService } from './cars-form-validation-helper.service';

@Injectable({
  providedIn: 'root'
})
export class CarsFormValidationService extends CarsFormValidationHelperService {
  setLocationError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    return form.controls['locationCode'].invalid
      ? content.errors.locationErrorMessage
      : '';
  }

  setPickupDateError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    return form.controls['pickUpDate'].invalid
      ? content.errors.dateErrorMessage
      : '';
  }

  setDropoffDateError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    const invalidError = this.getInvalidDateError(form, content);
    if (invalidError) return invalidError;

    const rangeError = this.getDateRangeError(form, content);
    if (rangeError) return rangeError;

    return this.getDateSelectionError(form, content);
  }

  private getInvalidDateError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    return form.controls['dropOffDate'].invalid
      ? content.errors.dateErrorMessage
      : '';
  }

  private getDateRangeError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    const isValid = validateDateRange(
      form.controls['pickUpDate'].value,
      form.controls['dropOffDate'].value
    );
    return !isValid
      ? content.errors.dateRangeErrorMessage || content.errors.dateErrorMessage
      : '';
  }

  private getDateSelectionError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    const dates = this.getFormDates(form);
    if (!dates) return '';

    return this.isDropoffBeforePickup(dates.pickup, dates.dropoff)
      ? content.errors.dateSelectionErrorMessage ||
          content.errors.dateErrorMessage
      : '';
  }

  private getFormDates(
    form: FormGroup<CarsWidgetFormType>
  ): { pickup: number; dropoff: number } | null {
    const pickupDate = form.controls['pickUpDate'].value;
    const dropoffDate = form.controls['dropOffDate'].value;

    if (!pickupDate || !dropoffDate) return null;

    return {
      pickup: new Date(pickupDate).setHours(0, 0, 0, 0),
      dropoff: new Date(dropoffDate).setHours(0, 0, 0, 0)
    };
  }

  private isDropoffBeforePickup(pickup: number, dropoff: number): boolean {
    return dropoff < pickup;
  }

  validateForm(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): {
    locationError: string;
    pickupDateError: string;
    pickupTimeError: string;
    dropoffDateError: string;
    dropoffTimeError: string;
  } {
    return {
      locationError: this.setLocationError(form, content),
      pickupDateError: this.setPickupDateError(form, content),
      pickupTimeError: this.setPickupTimeError(form, content),
      dropoffDateError: this.setDropoffDateError(form, content),
      dropoffTimeError: this.setDropoffTimeError(form, content)
    };
  }

  setPickupTimeError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    return this.getPickupTimeError(form, content);
  }

  getPickupTimeError(
    form: FormGroup<CarsWidgetFormType>,
    content: CarsWidget
  ): string {
    const isPickupTimeValid = validatePickupDropoffTimeNotPassed(
      form.controls['pickUpDate'].value,
      form.controls['pickUpTime'].value
    );
    return !isPickupTimeValid
      ? content.errors.pickupTimePassedErrorMessage || ''
      : '';
  }
}
